import _ from "lodash";
import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
  memo,
} from "react";
import { createPortal } from "react-dom";

import classes from "./TableEditor.scss";
import { useI18n } from "Hooks";
import PropTypes from "prop-types";

import {
  DndContext,
  KeyboardSensor,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";

import { getCoreRowModel, useReactTable } from "@tanstack/react-table";

import DraggableRow from "./DraggableRow";
import ColumnOptionsOverlay from "./ColumnOptionsOverlay";
import RowOptionsOverlay from "./RowOptionsOverlay";
import TableRow from "./DraggableRow/TableRow";
import DraggableCell from "./DraggableCell";
import TableCell from "./DraggableCell/TableCell";

import {
  DEFAULT_EDITOR_TOOLBAR_OPTIONS,
  getColumnProjectionPosition,
  getRowProjectionPosition,
} from "./utils";
import { moveElementInArray } from "Utils";
import { DEFAULT_COLUMN_WIDTH } from "../../ActionTableV2Utils";

/* 

 Field Mode
  - create -> All the inputs fields will be editable.
  - view -> This will make all things non-edi table.
 */

const ICON_HALF_SIZE = 10;
const TableEditor = props => {
  const {
    mode,
    value,
    updateTableData,
    editorToolbarOptions,
    testId,
    onFocusInputCell,
    onBlurInputCell,
  } = props;
  const { t } = useI18n();

  const isViewMode = mode === "view";

  // useRefs

  const scrollContainerRef = useRef(null);

  // row projection line reference
  const rowTargetLineRef = useRef(null);
  // All rows listener, attribute & node references
  const rowRefsAndHandlers = useRef({});

  // column projection line reference
  const columnTargetLineRef = useRef(null);
  // All columns listener, attribute & node references
  const colRefsAndHandlers = useRef({});

  // Reference to each header cell input, used to focus on newly added column header cell
  const headerCellInputRefs = useRef({});

  // Reference to each row node, used to get height of each row cells while dragging
  const rowRefs = useRef({});

  const setHeaderCellInputRefs = ({ columnId, ref }) => {
    headerCellInputRefs.current[columnId] = ref;
  };

  const setRowRefs = ({ rowId, ref }) => {
    rowRefs.current[rowId] = ref;
  };

  // Hooks

  const [columns, setColumns] = useState([]);
  const [data, setData] = useState(_.get(value, "data", {}));

  const [draggingRowId, setDraggingRowId] = useState(null);
  const [draggingColId, setDraggingColId] = useState(null);
  // hoveredRow and column will be used to show the drag handle near the hovered row/column
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const [columnOrder, setColumnOrder] = useState(() =>
    columns.map(c => _.get(c, "id"))
  );

  const [rowDragHandlePosition, setRowDragHandlePosition] = useState({
    top: 0,
  });
  const [columnDragHandlePosition, setColumnDragHandlePosition] = useState({
    left: 0,
  });

  // showColumnOptions is integer, will have column id, and will show dropdown near the column with same id as here
  // , -1 means no active column, no dropdown shown
  const [showColumnOptions, setShowColumnOptions] = useState(-1);
  // This will hold the positon of the column
  const [columnOptionsPosition, setColumnOptionsPosition] = useState({});
  // showRowOptions is integer, will have row id, and will show dropdown near the row with same id as here
  // , -1 means no active rows, no dropdown shown
  const [showRowOptions, setShowRowOptions] = useState(-1);
  // This will hold the positon of the row
  const [rowOptionsPosition, setRowOptionsPosition] = useState({});

  // useEffects

  // close other options if one is open
  useEffect(() => {
    if (showColumnOptions > -1) setShowRowOptions(-1);
  }, [showColumnOptions]);

  useEffect(() => {
    if (showRowOptions > -1) setShowColumnOptions(-1);
  }, [showRowOptions]);

  useEffect(() => {
    const tableData = value;
    const { columns, data } = tableData || {};
    if (!_.isEmpty(columns)) {
      setColumns(columns);
      setColumnOrder(() => columns.map(c => _.get(c, "id")));
    }

    if (_.size(data) === 1) {
      addRow(1);
    } else if (!_.isEmpty(data)) {
      setData(data);
    }
  }, [value]);

  const draggableColumnIds = useMemo(() => {
    const movableColumns = _.filter(columns, col => {
      return !col.isFixed;
    });
    return _.map(movableColumns, elem => _.get(elem, "id"));
  }, [columns]);

  // draggableRowIds used only in dragging stuff.
  const draggableRowIds = useMemo(() => {
    const movableRows = _.filter(data, row => !row.isFixed);
    return _.map(movableRows, row => row.id);
  }, [data]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    getRowId: row => _.get(row, "id"), //required because row indexes will change
    state: {
      columnOrder,
    },
    onColumnOrderChange: setColumnOrder,
    defaultColumn: {
      minSize: 80,
      size: DEFAULT_COLUMN_WIDTH,
    },
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        // fix handleDragStart was triggered the moment we click the handler
        distance: 5, // Enable handleDragStart function when dragging 5px away from the handle position
      },
    }),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const resizingColumnId = table.getState().columnSizingInfo.isResizingColumn;

  const handleColumnResize = () => {
    const resizedColumns = table.getState().columnSizing;
    // update the columns state with the new width
    const newColumns = columns.map(col => {
      const newWidth = resizedColumns[col.id] || col.size;
      return { ...col, size: newWidth };
    });
    setColumns(newColumns);
    updateTableData({
      columns: newColumns,
      columnOrder,
      data,
    });
  };

  useEffect(() => {
    table.options.dragHandleRefsCol = {};
  }, [table]);

  const findRowFallingInRange = mouseEvent => {
    // if we are scrolling horizontally, we don't want to show the row drag handle
    if (scrollContainerRef.current.scrollLeft > 0) return;
    const { pageY } = mouseEvent;

    const fallingRowId = _.find(draggableRowIds, rowId => {
      if (!rowRefsAndHandlers.current[rowId]) return false;
      const rowRect = rowRefsAndHandlers.current[
        rowId
      ].ref.current.getBoundingClientRect();
      // check if the mouse pointer is in the range of the row
      if (pageY >= rowRect.top && pageY <= rowRect.bottom) {
        return true;
      }
      return false;
    });
    if (fallingRowId) return rowRefsAndHandlers.current[fallingRowId];
  };

  const findColumnFallingInRange = mouseEvent => {
    // if we have scrolled till our top reaches above viewport, we don't want to show the column drag handle
    // get top of the scroll container + header height
    const scrollContainerRect = scrollContainerRef.current?.getBoundingClientRect();
    if (scrollContainerRect?.top - 52 <= 0) return;

    const { pageX } = mouseEvent;
    const fallingColumnId = _.find(columnOrder, colId => {
      const columnObj = colRefsAndHandlers.current[colId];
      // if we open 2 or more screen concurrently with different users and one user deletes a column, then the columnObj will be ref will be null
      if (!columnObj || !columnObj.ref.current) return false;
      const columnRect = columnObj.ref.current.getBoundingClientRect();
      if (columnRect && pageX >= columnRect.left && pageX <= columnRect.right) {
        return true;
      }
      return false;
    });
    if (fallingColumnId) return colRefsAndHandlers.current[fallingColumnId];
  };

  const closeRowOptionsHandler = () => {
    setHoveredRow(null);
    setRowDragHandlePosition({ top: -9999 });
  };

  const closeColumnOptionsHandler = () => {
    setHoveredColumn(null);
    setColumnDragHandlePosition({ left: -9999 });
  };

  const closeOptionHandlers = () => {
    closeRowOptionsHandler();
    closeColumnOptionsHandler();
  };

  const unsetDraggingIds = () => {
    setDraggingRowId(null);
    setDraggingColId(null);
  };

  const isCursorOutsideTable = event => {
    // check if cursor is outside the scrollable container with offset of 10px
    const { pageX, pageY } = event;
    const scrollContainerRect = scrollContainerRef.current.getBoundingClientRect();
    if (
      pageX < scrollContainerRect.left - 10 ||
      pageX > scrollContainerRect.right + 10 ||
      pageY < scrollContainerRect.top - 10 ||
      pageY > scrollContainerRect.bottom + 10
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    if (resizingColumnId || draggingRowId || draggingColId) {
      if (resizingColumnId) {
        // make the cursor resize while we are resizing
        scrollContainerRef.current.style.cursor = "col-resize";
      } else {
        // make the cursor grab while we are dragging
        // applying to document as our overlay is in dom level
        document.body.style.cursor = "grab";
      }
      onFocusInputCell();
    } else {
      scrollContainerRef.current.style.cursor = "auto";
      document.body.style.cursor = "auto";
      onBlurInputCell();
    }
  }, [draggingRowId, draggingColId, resizingColumnId]);

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const shouldTriggerEvents =
      !draggingColId && !draggingRowId && !resizingColumnId && !isViewMode;

    function onMouseMove(event) {
      if (!shouldTriggerEvents) return;
      if (showRowOptions === -1) {
        const rowInPointerRange = findRowFallingInRange(event);
        if (
          rowInPointerRange &&
          rowInPointerRange.id !== hoveredRow?.id &&
          scrollContainerRef.current
        ) {
          const targetRect = rowInPointerRange.ref.current.getBoundingClientRect();

          const offsetTop = scrollContainerRef.current.getBoundingClientRect()
            .top;

          // As the getBoundingClientRect gives the top of the element from the viewport, we need to subtract the top of the scroll container
          // as we want in the middle of the row we will divide the height by 2 and to make sure the icon is in the middle of the row we will subtract the half of the icon size
          const top =
            targetRect.top - offsetTop + targetRect.height / 2 - ICON_HALF_SIZE;

          setHoveredRow(rowInPointerRange);
          setRowDragHandlePosition({ top });
        }
      }
      if (showColumnOptions === -1) {
        const columnInPointerRange = findColumnFallingInRange(event);

        if (
          columnInPointerRange &&
          columnInPointerRange.id !== hoveredColumn?.id &&
          scrollContainerRef.current
        ) {
          const targetRect = columnInPointerRange.ref.current.getBoundingClientRect();
          const offsetLeft = scrollContainerRef.current.getBoundingClientRect()
            .left;

          // As the getBoundingClientRect gives the left of the element from the viewport, we need to subtract the left of the scroll container to get the position relative to the scroll container
          // as we want in the middle of the column we will divide the width by 2 and to make sure the icon is in the middle of the column we will subtract the half of the icon size
          // we will also add the scrollLeft of the scroll container to make sure the position is correct when we scroll horizontally
          const left =
            targetRect.left -
            offsetLeft +
            scrollContainerRef.current.scrollLeft +
            targetRect.width / 2 -
            ICON_HALF_SIZE;

          setHoveredColumn(columnInPointerRange);
          setColumnDragHandlePosition({ left });
        }
      }
    }

    const onScroll = () => {
      if (resizingColumnId || isViewMode) return;
      // if we scroll vertically and we are dragging a row, remove column dragHandle
      if (draggingRowId && !_.isNull(hoveredColumn)) {
        setHoveredColumn(null);
        setColumnDragHandlePosition({ left: -9999 });
      } else if (draggingColId && !_.isNull(hoveredRow)) {
        // if we scroll vertically and we are dragging a clmn, remove row dragHandle
        setHoveredRow(null);
        setRowDragHandlePosition({ top: -9999 });
      }
      if (!draggingColId && !draggingRowId) {
        closeOptionHandlers();
      }
    };

    const onMouseMoveOverWindow = event => {
      // if it is, we will hide the hovered row/column dragged handle which we are showing on mouse move
      if (!shouldTriggerEvents) return;
      if (isCursorOutsideTable(event)) {
        closeOptionHandlers();
      }
    };

    const handleOnClick = event => {
      // if we click outside the table, we will close the active row/column
      if (isCursorOutsideTable(event)) {
        setShowColumnOptions(-1);
        setShowRowOptions(-1);
      }
    };

    scrollContainerRef?.current?.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mousemove", onMouseMoveOverWindow);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("click", handleOnClick);

    return () => {
      scrollContainerRef?.current?.removeEventListener(
        "mousemove",
        onMouseMove
      );
      window.removeEventListener("mousemove", onMouseMoveOverWindow);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("click", handleOnClick);
    };
  }, [
    data,
    columnOrder,
    draggingRowId,
    draggingColId,
    showRowOptions,
    showColumnOptions,
    hoveredRow,
    hoveredColumn,
    resizingColumnId,
  ]);

  useEffect(() => {
    closeOptionHandlers();
  }, [mode]);

  const onDragEnd = ({ active, over }) => {
    if (columnTargetLineRef.current)
      columnTargetLineRef.current.style.opacity = "0";
    if (rowTargetLineRef.current) rowTargetLineRef.current.style.opacity = "0";

    const activeId = active.id;
    const overId = _.get(over, "id", null);

    const items = draggingColId ? columns : data;

    const fromIndex = _.findIndex(items, { id: activeId });
    const toIndex = _.findIndex(items, { id: overId });

    const isAnyItemMissing = fromIndex === -1 || toIndex === -1;
    if (!isAnyItemMissing && activeId !== overId) {
      const updatedValue = moveElementInArray({
        array: items,
        fromIndex,
        toIndex,
      });

      const updatedColumns = draggingColId ? updatedValue : columns;
      const updatedData = draggingRowId ? updatedValue : data;
      updateTableData({
        columns: updatedColumns,
        columnOrder: updatedColumns.map(col => col.id),
        data: updatedData,
      });
    }
    closeOptionHandlers();
    unsetDraggingIds();
  };

  const handleDragMove = ({ delta, active, over }) => {
    if (!over) return;

    const { id: overId, rect: overRect } = over;

    const scrollContainerElement = scrollContainerRef.current;
    if (!scrollContainerElement) return;

    // row projection
    if (draggingRowId) {
      const overItemIndex = _.findIndex(draggableRowIds, id => id === overId);
      if (overItemIndex === -1) return;

      const position = getRowProjectionPosition(delta, active, over);

      const { top, bottom } = overRect;

      const targetLineElem = rowTargetLineRef.current;

      const {
        width: scrollContainerWidth,
        top: offsetTop,
      } = scrollContainerElement.getBoundingClientRect();

      const yValue = (position === "top" ? top : bottom) - offsetTop;

      const tableTotalWidth = table.getTotalSize();

      // handle the width when we don't have scrollbar and the container has limited width
      const width =
        tableTotalWidth >= scrollContainerWidth
          ? scrollContainerWidth
          : tableTotalWidth;
      targetLineElem.style.transform = `translate(0px, ${yValue}px)`;

      // set the width of the target line to match the width of the table
      targetLineElem.style.width = `${width}px`;
      targetLineElem.style.opacity = "1";
    } else if (draggingColId) {
      // column projection
      const overItemIndex = _.findIndex(columnOrder, id => id === overId);
      if (overItemIndex === -1) return;

      const position = getColumnProjectionPosition(delta, active, over);

      const targetLineElem = columnTargetLineRef.current;
      if (!targetLineElem) return;

      const offsetLeft = scrollContainerElement.getBoundingClientRect().left;

      const { left, right } = overRect;

      const height = scrollContainerElement.clientHeight;

      const xValue =
        (position === "left" ? left : right) -
        offsetLeft +
        scrollContainerElement.scrollLeft;

      targetLineElem.style.transform = `translate(${xValue}px, ${ICON_HALF_SIZE}px)`;

      targetLineElem.style.height = `${height - ICON_HALF_SIZE}px`;
      targetLineElem.style.opacity = "1";
      targetLineElem.style.width = "4px";
    }
  };

  const handleDragStart = useCallback(
    event => {
      const { active } = event;
      const activeId = _.get(active, "id");
      if (_.includes(draggableRowIds, activeId)) {
        setDraggingRowId(activeId);
        setShowRowOptions(-1);
      } else if (_.includes(columnOrder, activeId)) {
        setDraggingColId(activeId);
        setShowColumnOptions(-1);
      }
    },
    [draggableRowIds, columnOrder]
  );

  const handleDragCancel = e => {
    unsetDraggingIds();
  };

  const addCol = colIndex => {
    let maxId = _.size(columns) + 1;
    _.forEach(columnOrder, col => {
      if (maxId <= col) maxId = parseInt(col) + 1;
    });
    const columnId = _.toString(maxId);
    // Create the new column
    const newColumn = {
      accessorKey: `col${maxId}`,
      cell: info => info.getValue(),
      id: columnId,
      size: DEFAULT_COLUMN_WIDTH,
    };

    // Insert the new column at the specified index
    const newCols = [
      ...columns.slice(0, colIndex),
      newColumn,
      ...columns.slice(colIndex),
    ];
    // Update columnOrder
    const newColumnOrder = [
      ...columnOrder.slice(0, colIndex),
      columnId,
      ...columnOrder.slice(colIndex),
    ];

    setColumns(newCols);
    setColumnOrder(newColumnOrder);

    // Insert new column data at the specified index for each row
    const newData = _.cloneDeep(data);
    const newHeaderCellValue = t("project:column_number", {
      number: _.size(newCols) - 1,
    });
    _.forEach(newData, row => {
      row[`col${maxId}`] = row.isHeader ? newHeaderCellValue : "";
    });

    closeColumnOptionsHandler();
    // closing column options if it is open
    setShowColumnOptions(-1);
    setData(newData);
    updateTableData({
      columns: newCols,
      columnOrder: newColumnOrder,
      data: newData,
    });

    // Focus newly added column header cell
    setTimeout(() => {
      const newInputTextRef = headerCellInputRefs.current[columnId];
      if (newInputTextRef) {
        newInputTextRef.focus();
        newInputTextRef.select();
      }
    }, 100);
  };

  const deleteCol = id => {
    const column = _.find(columns, col => col.id === id);

    if (!column || _.size(columns) < 2) return;
    const newCols = _.filter(columns, col => col.id !== id);
    const newColumnOrder = _.filter(columnOrder, colId => colId !== id);

    // Remove the corresponding key from each row in data
    const columnToDelete = column.accessorKey;
    const newData = _.map(data, row => {
      const { [columnToDelete]: _, ...remainingData } = row;
      return remainingData;
    });

    setColumns(newCols);
    setColumnOrder(newColumnOrder);
    setData(newData);

    closeColumnOptionsHandler();
    // close the columnOptions
    setShowColumnOptions(-1);

    updateTableData({
      columns: newCols,
      columnOrder: newColumnOrder,
      data: newData,
    });
  };

  const addRow = rowIndex => {
    if (isViewMode) return;
    // Create the new row
    let maxId = _.size(data) + 1;
    _.forEach(data, row => {
      if (maxId <= row.id) maxId = parseInt(row.id) + 1;
    });
    const newRow = { id: maxId };
    _.forEach(columns, col => {
      newRow[_.get(col, "accessorKey")] = "";
    });

    // Insert the new row at the specified index
    const newData = [
      ...data.slice(0, rowIndex),
      newRow,
      ...data.slice(rowIndex),
    ];

    setData(newData);

    // Update the table question data
    updateTableData({
      columns: columns,
      columnOrder: columnOrder,
      data: newData,
    });

    // Focus on the first input cell of the newly added row
    setTimeout(() => {
      const rowId = _.get(newRow, "id");
      const rowRef = document.getElementById(`Row_${rowId}`);
      if (!rowRef) return;
      // as first column will always be checkbox column
      const cellRef = rowRef?.children[1];
      if (!cellRef || !cellRef.children[0]) return;
      cellRef.children[0].focus();
    });

    closeRowOptionsHandler();
    // Closing row options if it is open
    setShowRowOptions(-1);
  };

  const deleteRow = rowId => {
    const newData = _.filter(data, row => row.id !== rowId);
    if (_.size(newData) === _.size(data) || _.size(data) < 2) return;

    setData(newData);

    updateTableData({
      columns: columns,
      columnOrder: columnOrder,
      data: newData,
    });

    closeRowOptionsHandler();
    // Close the row options if it is open
    setShowRowOptions(-1);
  };

  const setCellValue = useCallback(
    ({ rowId, colId, val }) => {
      // Find the index of the target row based on its id
      const rowIndex = data.findIndex(row => row.id === rowId);

      if (rowIndex === -1) return;
      // Create a deep copy of the target row to avoid mutating other rows
      const updatedRow = { ...data[rowIndex] };

      // Update the specific cell value
      updatedRow[colId] = val;

      // Create a shallow copy of the data array, replacing only the updated row
      const updatedData = [
        ...data.slice(0, rowIndex),
        updatedRow,
        ...data.slice(rowIndex + 1),
      ];

      setData(updatedData);
      updateTableData({
        columnOrder: columnOrder,
        columns: columns,
        data: updatedData,
      });
    },
    [data, columns, columnOrder]
  );

  const isCreateMode = _.isEqual(mode, "create");

  const renderRowDraggingOverlay = () => {
    const row = _.find(table.getRowModel().rows, r => r.id === draggingRowId);
    if (!row) return null;

    const tableRowStyles = {
      backgroundColor: "var(--surface-primary-enabled)",
    };

    return (
      <TableRow
        key={row.id}
        mode={mode}
        row={row}
        testId={testId}
        tableRowStyles={tableRowStyles}
        isRowDragging={true}
        showRowOptions={showRowOptions}
      />
    );
  };

  const renderColumnDraggingOverlay = () => {
    const colId = draggingColId;
    return (
      <div className={classes.column}>
        {table.getRowModel().rows.map((row, rowIndex) => {
          const cell = row._getAllCellsByColumnId()[colId];
          const rowRef = rowRefs.current[row.id];
          // this is necessary as repainting the whole overlay does not have the
          // same cell height which is rendered in the actual table DOM element
          // for a dragging column there could be a cell which don't required any height due to empty data but there can be case where
          // on the same row level there is a cell which has data and required more than the default height
          const height = rowRef
            ? rowRef.current.getBoundingClientRect().height
            : "";
          const tableCellStyles = {
            height,
          };
          const isHeader = row.original.isHeader;
          return (
            <TableCell
              key={cell.id}
              cell={cell}
              mode={mode}
              rowIndex={rowIndex}
              testId={testId}
              isHeader={isHeader}
              tableCellStyles={tableCellStyles}
              isColumnDragging={true}
              isLastRow={_.isEqual(row.id, _.last(data).id)}
            />
          );
        })}
      </div>
    );
  };

  const onShowRowOptionsButtonClick = useCallback(() => {
    setShowRowOptions(hoveredRow.id);
    setRowOptionsPosition({
      top: rowDragHandlePosition.top,
      rowIndex: _.findIndex(data, { id: hoveredRow.id }),
      rowId: hoveredRow.id,
    });
  }, [hoveredRow, rowDragHandlePosition]);

  const onShowColumnOptionsButtonClick = useCallback(() => {
    setShowColumnOptions(hoveredColumn.id);
    setColumnOptionsPosition({
      left: columnDragHandlePosition.left,
      colIndex: _.indexOf(columnOrder, hoveredColumn.id),
      columId: hoveredColumn.id,
    });
  }, [hoveredColumn, columnDragHandlePosition]);

  return (
    <div className={classes.mainContainer}>
      <div
        className={classes.tableAndOverlayContainer}
        ref={scrollContainerRef}
      >
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <DndContext
              onDragStart={handleDragStart}
              onDragEnd={onDragEnd}
              onDragMove={handleDragMove}
              onDragCancel={handleDragCancel}
              sensors={sensors}
            >
              <SortableContext
                items={draggableColumnIds}
                strategy={rectSortingStrategy}
                // disabling because it was making collisions with the row drag
                disabled={!_.isNull(draggingRowId)}
              >
                <thead>
                  {table.getHeaderGroups().map(headerGroup => {
                    const rowId = _.first(data)?.id;
                    const targetRow = table
                      .getRowModel()
                      .rows.find(row => row.id === rowId);
                    return (
                      <tr
                        key={headerGroup.id}
                        ref={ref =>
                          setRowRefs({ rowId, ref: { current: ref } })
                        }
                      >
                        {headerGroup.headers.map((header, index) => {
                          const headerCell = targetRow
                            ? _.first(
                                targetRow
                                  .getVisibleCells()
                                  .filter(cell => cell.column.id === header.id)
                              )
                            : {};
                          const isFixed = _.get(
                            header,
                            "column.columnDef.isFixed",
                            false
                          );
                          return (
                            <DraggableCell
                              key={header.id}
                              cell={headerCell}
                              rowId={rowId}
                              setCellValue={setCellValue}
                              mode={mode}
                              colIndex={index}
                              rowIndex={0}
                              setShowColumnOptions={setShowColumnOptions}
                              setShowRowOptions={setShowRowOptions}
                              showColumnOptions={showColumnOptions}
                              editorToolbarOptions={editorToolbarOptions}
                              testId={testId}
                              colRefsAndHandlers={colRefsAndHandlers}
                              isHeader={true}
                              header={header}
                              handleColumnResize={handleColumnResize}
                              isDraggable={!isFixed}
                              setHeaderCellInputRefs={setHeaderCellInputRefs}
                              onFocusInputCell={onFocusInputCell}
                              onBlurInputCell={onBlurInputCell}
                              resizingColumnId={resizingColumnId}
                            />
                          );
                        })}
                      </tr>
                    );
                  })}
                </thead>
              </SortableContext>
              {createPortal(
                <DragOverlay
                  zIndex={1002}
                  // currently placing null here, as animation was going back to the drag start place
                  dropAnimation={null}
                >
                  {draggingRowId && renderRowDraggingOverlay()}
                  {draggingColId && renderColumnDraggingOverlay()}
                </DragOverlay>,
                document.body
              )}
              <tbody className={classes.tbody}>
                <SortableContext
                  items={draggableRowIds}
                  strategy={rectSortingStrategy}
                  disabled={!_.isNull(draggingColId)}
                >
                  {table
                    .getRowModel()
                    .rows.filter(row => !row.original.isHeader)
                    .map((row, rowIndex) => {
                      return (
                        <DraggableRow
                          setCellValue={setCellValue}
                          mode={mode}
                          key={row.id}
                          row={row}
                          columnOrder={columnOrder}
                          // as first row is header row, so we are starting index from 1
                          rowIndex={rowIndex + 1}
                          setShowColumnOptions={setShowColumnOptions}
                          showColumnOptions={showColumnOptions}
                          setShowRowOptions={setShowRowOptions}
                          showRowOptions={showRowOptions}
                          editorToolbarOptions={editorToolbarOptions}
                          testId={testId}
                          rowRefsAndHandlers={rowRefsAndHandlers}
                          colRefsAndHandlers={colRefsAndHandlers}
                          setRowRefs={setRowRefs}
                          isLastRow={_.isEqual(row.id, _.last(data).id)}
                          onFocusInputCell={onFocusInputCell}
                          onBlurInputCell={onBlurInputCell}
                          resizingColumnId={resizingColumnId}
                        />
                      );
                    })}
                </SortableContext>
              </tbody>
            </DndContext>
          </table>
          {/* Making columnHandle bar and vertical targetLine here because we want column handlebars to be relative of any child of horizontal scroll container */}
          <div
            style={{
              top: 0,
              left: columnDragHandlePosition.left,
              zIndex: 1000,
            }}
            {...hoveredColumn?.listeners}
            {...hoveredColumn?.attributes}
            ref={hoveredColumn?.setActivatorNodeRef}
            data-test-id={`${testId}-columnHandle`}
            className={classes.dragHandle}
          >
            {/* {hoveredColumn ? (
              <IconButton
                icon={<DotsSixVerticalOutlined />}
                size="x-small"
                type="outlined"
                variant="neutral"
                isCompact
                className={classNames(
                  classes.dragHandleButtonContainer,
                  classes.rotatedIconButton
                )}
                onClick={onShowColumnOptionsButtonClick}
                isActivated={
                  !_.isNull(draggingColId) ||
                  (showColumnOptions > -1 && !_.isEmpty(columnOptionsPosition))
                }
              />
            ) : null} */}
          </div>
          {/* Column Options will be visible when row drag handle is clicked */}
          {showColumnOptions > -1 && !_.isEmpty(columnOptionsPosition) && (
            <div
              style={{
                position: "absolute",
                top: 16,
                left: _.get(columnOptionsPosition, "left") - 50,
              }}
            >
              <ColumnOptionsOverlay
                deleteCol={deleteCol}
                index={_.get(columnOptionsPosition, "colIndex")}
                id={_.get(columnOptionsPosition, "columId")}
                addCol={addCol}
                t={t}
                testId={testId}
              />
            </div>
          )}
          <div
            className={classes.droppingTargetLine}
            ref={columnTargetLineRef}
          />
        </div>

        {/* Row Options will be visible when col header drag handle is clicked */}
        {showRowOptions > -1 && !_.isEmpty(rowOptionsPosition) && (
          <div
            style={{
              position: "absolute",
              top: _.get(rowOptionsPosition, "top") + 16,
              left: -ICON_HALF_SIZE,
            }}
          >
            <RowOptionsOverlay
              deleteRow={deleteRow}
              index={_.get(rowOptionsPosition, "rowIndex")}
              id={_.get(rowOptionsPosition, "rowId")}
              addRow={addRow}
              t={t}
              testId={testId}
            />
          </div>
        )}

        {/* Row drag handler */}
        {/* This position is relative to TableEditor's parent component */}
        {/* The scrollbar is also on parent level  */}
        <div
          style={{
            top: rowDragHandlePosition.top,
            left: -ICON_HALF_SIZE,
            zIndex: 1000,
          }}
          {...hoveredRow?.listeners}
          {...hoveredRow?.attributes}
          ref={hoveredRow?.setActivatorNodeRef}
          data-test-id={`${testId}-rowHandle`}
          className={classes.dragHandle}
        >
          {/* {hoveredRow ? (
            <IconButton
              icon={<DotsSixVerticalOutlined />}
              size="x-small"
              isCompact
              type="outlined"
              variant="neutral"
              className={classes.dragHandleButtonContainer}
              onClick={onShowRowOptionsButtonClick}
              isActivated={
                !_.isNull(draggingRowId) ||
                (showRowOptions > -1 && !_.isEmpty(rowOptionsPosition))
              }
            />
          ) : null} */}
        </div>
        <div className={classes.droppingTargetLine} ref={rowTargetLineRef} />
      </div>

      {/* {isCreateMode ? (
        <div className={classes.footerButtonContainer}>
          <Button
            type="outlined"
            variant="neutral"
            onClick={() => addRow(_.size(data))}
            icon={<AddRowOutlined />}
            testId={`${testId}-table-addRow-footer-button`}
          >
            {t("project:add_row")}
          </Button>
          <Button
            type="outlined"
            variant="neutral"
            onClick={() => addCol(_.size(columns))}
            size="medium"
            icon={<AddColumnOutlined />}
            testId={`${testId}-table-addColumn-footer-button`}
          >
            {t("project:add_column")}
          </Button>
        </div>
      ) : null} */}
    </div>
  );
};

TableEditor.displayName = "TableEditor";

TableEditor.defaultProps = {
  mode: "create",
  value: {},
  updateTableData: () => {},
  testId: "UiComponent",
  editorToolbarOptions: DEFAULT_EDITOR_TOOLBAR_OPTIONS,
};

TableEditor.propTypes = {
  mode: PropTypes.string,
  value: PropTypes.object,
  updateTableData: PropTypes.func,
  testId: PropTypes.string,
  editorToolbarOptions: PropTypes.array,
};
export default memo(TableEditor);
